import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import {
  createColumnHelper,
  createCoreRowModel,
  createSortedRowModel,
  flexRender,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { AlertTriangle, ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { get } from '~/lib/api'
import type { ConversationRow, ConversationsPayload, Stage, WaitingOn } from '~/lib/types'
import { formatAgo } from '~/lib/format'
import StageBadge, { STAGE_LABELS } from '~/components/StageBadge'
import DemoBadge from '~/components/DemoBadge'
import Spinner from '~/components/Spinner'
import { LIVE_REFETCH_MS } from '~/lib/queryClient'
import { Button } from '~/components/ui/button'
import { Card, CardContent } from '~/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'

export const conversationsQuery = {
  queryKey: ['conversations'],
  queryFn: () => get<ConversationsPayload>('/api/v1/conversations'),
  refetchInterval: LIVE_REFETCH_MS,
}

const features = tableFeatures({
  rowSortingFeature,
  coreRowModel: createCoreRowModel(),
  sortedRowModel: createSortedRowModel(),
})
const column = createColumnHelper<typeof features, ConversationRow>()

const FILTERS: { value: Stage | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'initiated', label: STAGE_LABELS.initiated },
  { value: 'engaged', label: STAGE_LABELS.engaged },
  { value: 'interested', label: STAGE_LABELS.interested },
  { value: 'converted', label: STAGE_LABELS.converted },
  { value: 'lost', label: STAGE_LABELS.lost },
]

export const WAITING_LABELS: Record<WaitingOn, string> = {
  agent: 'Agent replying',
  student: 'Awaiting student',
  human: 'Needs a human',
  none: '—',
}

export default function Conversations() {
  const search = useSearch({ from: '/app/conversations' })
  const navigate = useNavigate()
  const [sorting, setSorting] = useState<SortingState>([])

  const { data, isPending } = useQuery(conversationsQuery)

  const rows = useMemo(() => {
    const all = data?.conversations ?? []
    return all.filter(
      (row) =>
        (search.stage === 'all' || row.stage === search.stage) &&
        (!search.escalated || row.escalated),
    )
  }, [data, search.stage, search.escalated])

  // ColumnDef is invariant in its value type and each accessor infers its own,
  // so the array is widened once here rather than at every use site.
  const columns = useMemo<ColumnDef<typeof features, ConversationRow, any>[]>(
    () => [
      column.accessor('student', {
        header: 'Student',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-medium">
              {row.original.student}
              {row.original.escalated && (
                <AlertTriangle className="size-3.5 text-warning" aria-label="Escalated" />
              )}
            </p>
            <p className="truncate text-xs text-faint">{row.original.last_message}</p>
          </div>
        ),
      }),
      column.accessor('course', { header: 'Course' }),
      column.accessor('stage', {
        header: 'Stage',
        cell: ({ getValue }) => <StageBadge stage={getValue()} />,
      }),
      column.accessor('waiting_on', {
        header: 'Waiting on',
        cell: ({ getValue }) => (
          <span className="text-ink-2">{WAITING_LABELS[getValue() as WaitingOn]}</span>
        ),
      }),
      column.accessor('minutes_ago', {
        header: 'Last activity',
        cell: ({ getValue }) => (
          <span className="[font-variant-numeric:tabular-nums]">{formatAgo(getValue())}</span>
        ),
      }),
    ],
    [],
  )

  const table = useTable<typeof features, ConversationRow>({
    features,
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Conversations</h1>
          <p className="mt-1 text-sm text-ink-2">
            Every thread the agent is running, and who each one is waiting on.
          </p>
        </div>
        {data?.demo && <DemoBadge />}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            onClick={() => navigate({ to: '/conversations', search: { ...search, stage: filter.value } })}
            aria-pressed={search.stage === filter.value}
            className={`rounded-lg border px-3 py-1.5 text-xs transition ${
              search.stage === filter.value
                ? 'border-line bg-surface font-medium text-ink shadow-xs'
                : 'border-transparent text-ink-2 hover:bg-surface hover:text-ink'
            }`}
          >
            {filter.label}
          </button>
        ))}

        <span className="mx-1 h-4 w-px bg-line" aria-hidden="true" />

        <Button
          variant={search.escalated ? 'default' : 'outline'}
          size="sm"
          onClick={() =>
            navigate({ to: '/conversations', search: { ...search, escalated: !search.escalated } })
          }
          aria-pressed={search.escalated}
        >
          <AlertTriangle className="size-3.5" aria-hidden="true" />
          Escalated only
        </Button>

        <span className="ml-auto text-xs text-faint">
          {rows.length} conversation{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {isPending ? (
            <div className="p-8">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const sorted = header.column.getIsSorted()
                      const Icon =
                        sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ChevronsUpDown

                      return (
                        <TableHead key={header.id} className="px-5">
                          <button
                            type="button"
                            onClick={header.column.getToggleSortingHandler()}
                            className="inline-flex items-center gap-1.5 hover:text-ink"
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <Icon className="size-3" aria-hidden="true" />
                          </button>
                        </TableHead>
                      )
                    })}
                    <TableHead className="w-10" />
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="group relative hover:bg-surface-2">
                    {row.getAllCells().map((cell, index) => (
                      <TableCell key={cell.id} className="px-5 py-3">
                        {index === 0 ? (
                          // Stretched link: the whole row is the hit target, but
                          // the sortable headers above stay clickable.
                          <Link
                            to="/conversations/$conversationId"
                            params={{ conversationId: row.original.id }}
                            search={search}
                            className="after:absolute after:inset-0 after:content-['']"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Link>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="pr-5">
                      <ChevronRight className="size-4 text-faint" aria-hidden="true" />
                    </TableCell>
                  </TableRow>
                ))}

                {table.getRowModel().rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} className="py-10 text-center text-faint">
                      No conversations match this filter.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
